import { defineStore } from "pinia";
import { watch, ref } from "vue";
import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
} from "firebase/auth";
import { useFirebaseAuth } from "vuefire";
import { useLocalStorage } from "@vueuse/core";
import { useRoute } from "vue-router";
import { FirebaseError } from "firebase/app";
import { AuthErrorCode } from "@/enums.ts";
import { handleError, AUTH_ACTIONS } from "@/stores/auth.errors";
import { logger, LogCode } from "@/logger";
import { createCorrelationId } from "@/lib/correlationId";

const MAX_MAGIC_LINK_RETRIES = 2;

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

const isRetryableMagicLinkError = (error: unknown) => {
  if (!(error instanceof FirebaseError)) {
    return false;
  }

  return (
    error.code === AuthErrorCode.NETWORK_REQUEST_FAILED ||
    (error.code === AuthErrorCode.INTERNAL_ERROR &&
      /deadline exceeded|timed out|timeout/i.test(error.message))
  );
};

export const useAuthStore = defineStore("auth", () => {
  const route = useRoute();
  const auth = useFirebaseAuth()!;
  const emailForSignin = useLocalStorage("emailForSignin", "");
  // Initialize based on whether we have email in local storage
  const emailSent = ref(!!emailForSignin.value);

  watch(emailForSignin, (newEmail) => {
    // set emailSent to false if email is cleared from local storage
    if (!newEmail) {
      emailSent.value = false;
    }
  });

  const clearEmailForSignin = () => {
    emailForSignin.value = "";
  };

  const setEmailForSignin = (email: string) => {
    emailForSignin.value = email;
  };

  const resendEmail = async () => await sendEmail(emailForSignin.value);

  async function signout() {
    try {
      clearEmailForSignin();
      await auth.signOut();
      return;
    } catch (e) {
      handleError(e, AUTH_ACTIONS.SIGN_OUT, createCorrelationId());
    }
  }

  async function sendEmail(email: string) {
    emailForSignin.value = email;
    const correlationId = createCorrelationId();
    const actionCodeSettings = {
      // Post sign-in URL.
      url: `${globalThis.location.origin}/auth?redirect=${encodeURIComponent(
        typeof route.query.redirect === "string" ? route.query.redirect : "/"
      )}&correlationId=${correlationId}`,
      // This must be true.
      handleCodeInApp: true,
    };
    try {
      await sendSignInLinkToEmail(
        auth,
        emailForSignin.value,
        actionCodeSettings
      );
      emailSent.value = true;
      logger.info(
        `Sign-in email link sent to: ${emailForSignin.value}`,
        {
          component: 'stores/auth.ts',
          pathname: window.location.pathname,
          route: window.location.pathname + window.location.search,
          logCode: LogCode.SENT_EMAIL_LINK,
          correlationId,
          email: emailForSignin.value,
          
        },
      );
      return;
    } catch (e) {
      emailSent.value = false;
      handleError(e, AUTH_ACTIONS.SEND_EMAIL_LINK, correlationId);
    }
  }

  async function signInWithEmailResult() {
    const action = AUTH_ACTIONS.SIGN_IN_WITH_EMAIL_LINK
    const correlationId = typeof route.query.correlationId === "string"
      ? route.query.correlationId
      : createCorrelationId();
    if (emailForSignin.value === "") {
      handleError(new FirebaseError(AuthErrorCode.CUSTOM_MISSING_SESSION, ''), action, correlationId);
    }
    if (!isSignInWithEmailLink(auth, route.fullPath)) {
      handleError(new FirebaseError(AuthErrorCode.CUSTOM_INVALID_SIGNIN_LINK, ''), action ,correlationId);
    }
    try {
      for (let retryCount = 0; ; retryCount++) {
        try {
          await signInWithEmailLink(
            auth,
            emailForSignin.value,
            route.fullPath
          );

          clearEmailForSignin();
          return;
        } catch (error) {
          const hasRetriesRemaining =
            retryCount < MAX_MAGIC_LINK_RETRIES;

          if (!isRetryableMagicLinkError(error) || !hasRetriesRemaining) {
            throw error;
          }

          await wait((retryCount + 1) * 500);
        }
      }
    } catch (error) {
      handleError(error, action, correlationId);
    }
  }

  return {
    clearEmailForSignin,
    setEmailForSignin,
    emailForSignin,
    emailSent,
    signout,
    resendEmail,
    sendEmail,
    signInWithEmailResult,
  };
});